import React, { useState } from 'react';
import { Pressable } from 'react-native';

import { BrandColor, toRGBA } from '@styleseat/brand';
import { Box, Inline } from '@styleseat/ui';

import {
  Absolute,
  BodyText,
  HeaderText,
  Stack,
} from '../../../ui';
import { BasicStarIcon } from '../../../ui/Icons';
import LazyImage from '../../../ui/LazyImage';

import { ProviderService } from '../../../../api/Providers/Services';
import Currency from '../../../../modules/Currency';

import BookButton from '../BookButton';
import { MAX_TOP_SERVICE_IMAGES } from '../constants';
import { ProviderServiceReviewsSummaryState } from '../../../../store/ProviderReviewsSummary.model';

const MAX_DESCRIPTION_LENGTH = 100;

const serviceDurationFormatted = (minutes: number) => {
  const hours = Math.floor(minutes / 60);
  const min = minutes % 60;
  return hours > 0 ? `${hours} hr ${min} min` : `${minutes} min`;
};

const truncate = (text: string) => (text.length > MAX_DESCRIPTION_LENGTH
  ? `${text.substring(0, MAX_DESCRIPTION_LENGTH)}...`
  : text
);

export type ProviderServiceRevamp = ProviderService & {
  serviceRank?: number;
  numRatings?: number;
  averageStars?: string;
  imageUrls?: string[];
  numTotalImages?: number;
  isHighlightedService?: boolean;
};

export type ServiceItemProps = {
  service: ProviderServiceRevamp;
  providerId: number;
  providerPhone: string;
  isOnlineBookable: boolean;
  showAndUp?: boolean;
  onRatingsPhotosClick?: (service: ProviderService) => void;
  displayPhotos?: boolean;
  isLastBookedService?: boolean;
  serviceReviewSummary?: ProviderServiceReviewsSummaryState;
};

export const ServiceItem: React.FC<ServiceItemProps> = ({
  service,
  providerId,
  providerPhone,
  isOnlineBookable,
  showAndUp = false,
  onRatingsPhotosClick = () => {},
  displayPhotos = true,
  isLastBookedService = false,
  serviceReviewSummary,
}) => {
  const [
    showFullDescription,
    setShowFullDescription,
  ] = useState(service.blurb?.length < MAX_DESCRIPTION_LENGTH);
  const serviceDuration = serviceDurationFormatted(
    service.total_duration_minutes || service.duration_minutes,
  );

  const handleRatingsPhotosClick = () => {
    onRatingsPhotosClick(service);
  };

  const hasServiceReview = serviceReviewSummary?.number_of_reviews > 0 || service.numRatings > 0;
  const hasServicePhotos = displayPhotos && service.imageUrls?.length > 0;

  return (
    <Stack
      paddingY={24}
      space={16}
      borderStyle="solid"
      borderBottomWidth={1}
      borderBottomColor={BrandColor.LightGrey}
      testID="profile-service-item-revamp-card"
    >
      <Inline>
        <Stack space={8} flexShrink={1}>
          <HeaderText level={4} testID="profile-service-item-revamp-service-name">
            {service.name}
          </HeaderText>
          <Inline style={{ gap: '8px' } as any} flexWrap="wrap" alignItems="center">
            {service.isHighlightedService && (
              <Box
                paddingX={8}
                paddingY={4}
                borderRadius={100}
                color={BrandColor.LightBlue}
                testID="profile-service-item-revamp-highlighted-service"
              >
                <BodyText appearance="small" color={BrandColor.Metal}>
                  Highlighted
                </BodyText>
              </Box>
            )}
            {service.serviceRank && (
              <Box
                paddingX={8}
                paddingY={4}
                borderRadius={100}
                color={BrandColor.FloraLight}
                testID="profile-service-item-revamp-top-service-rank"
              >
                <BodyText appearance="small" color={BrandColor.Metal}>
                  {`#${service.serviceRank} booked`}
                </BodyText>
              </Box>
            )}
            {isLastBookedService && (
              <Box
                paddingX={8}
                paddingY={4}
                borderRadius={100}
                color={BrandColor.FloraLight}
                testID="profile-service-item-revamp-last-booked-badge"
              >
                <BodyText appearance="small" color={BrandColor.Metal}>
                  Last Booked
                </BodyText>
              </Box>
            )}
            <Inline space={8} alignItems="center">
              <BodyText appearance="small" color={BrandColor.Metal} testID="profile-service-item-revamp-cost">
                {/* eslint-disable-next-line max-len */}
                {Currency.shortFormat(service.cost)} {showAndUp && <BodyText color={BrandColor.Ash}>+</BodyText>}
              </BodyText>
              <BodyText appearance="small" color={BrandColor.Silver}>&#183;</BodyText>
              <BodyText appearance="small" color={BrandColor.Ash} testID="profile-service-item-revamp-duration">
                {serviceDuration}
              </BodyText>
            </Inline>
          </Inline>
        </Stack>
        <Inline
          flexGrow={1}
          justifyContent="flex-end"
          alignItems="center"
          paddingLeft={8}
        >
          <BookButton
            bookText="See Times"
            style={{
              display: 'flex',
              height: 32,
              textWrap: 'nowrap',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            size="medium"
            isOnlineBookable={isOnlineBookable}
            service={service}
            providerId={providerId}
            providerPhone={providerPhone}
            testID="profile-service-item-revamp-book-button"
          />
        </Inline>
      </Inline>
      {!!service.blurb && (
        <Pressable
          onPress={() => setShowFullDescription(!showFullDescription)}
          testID="profile-service-item-revamp-description"
        >
          <BodyText color={BrandColor.Ash}>
            {showFullDescription ? service.blurb : truncate(service.blurb)}
          </BodyText>
        </Pressable>
      )}
      {(hasServiceReview || hasServicePhotos) && (
        <Pressable onPress={handleRatingsPhotosClick}>
          <Inline justifyContent="flex-start" space={4}>
            {hasServiceReview && (
              <Inline
                paddingX={10}
                paddingY={12}
                space={4}
                borderRadius={100}
                alignItems="center"
                color={BrandColor.SuperLight}
                testID="profile-service-item-revamp-rating-badge"
              >
                <BasicStarIcon height={16} width={16} color={BrandColor.Metal} />
                <BodyText appearance="small" color={BrandColor.Metal}>
                  {service.averageStars || serviceReviewSummary.average}
                </BodyText>
                <BodyText appearance="small" color={BrandColor.Ash}>{`(${service.numRatings || serviceReviewSummary.number_of_reviews})`}</BodyText>
              </Inline>
            )}
            {hasServicePhotos && (
              <Inline space={4} testID="profile-service-item-revamp-images">
                {service.imageUrls.map((imageUrl: string, index) => (
                  <Box>
                    <LazyImage
                      key={service.id}
                      source={imageUrl}
                      description={service.name}
                      width={40}
                      height={40}
                      showPlaceholder
                      placeholderColor={BrandColor.LightGrey}
                      borderRadius={4}
                    />
                    {index === service.imageUrls.length - 1
                    && service.numTotalImages > MAX_TOP_SERVICE_IMAGES
                    && (
                      <Absolute
                        top={0}
                        bottom={0}
                        left={0}
                        right={0}
                      >
                        <Box
                          flex={1}
                          justifyContent="center"
                          alignItems="center"
                          color={toRGBA(BrandColor.BackInBlack, 0.45)}
                        >
                          <BodyText appearance="small" color={BrandColor.WhiteWash}>
                            {`+${service.numTotalImages - MAX_TOP_SERVICE_IMAGES}`}
                          </BodyText>
                        </Box>
                      </Absolute>
                    )}
                  </Box>
                ))}
              </Inline>
            )}
          </Inline>
        </Pressable>
      )}
    </Stack>
  );
};
